import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ScanFace, Stethoscope, MapPin, Sun } from "lucide-react";
import { ASSETS } from "@/lib/site";

const SECTIONS = [
  { label: "Glutathione", hash: "#glutathione" },
  { label: "Why Relumia", hash: "#why" },
  { label: "Authenticity", hash: "#authenticity" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    const scroll = () => {
      const el = document.querySelector(hash);
      if (el) {
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
        else el.scrollIntoView({ behavior: "smooth" });
      }
    };
    if (location.pathname === "/") scroll();
    else {
      navigate("/");
      setTimeout(scroll, 500);
    }
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-white/70 border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src={ASSETS.wordmark} alt="Relumia" className="h-9 md:h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {SECTIONS.map((n) => (
            <a
              key={n.hash}
              href={`/${n.hash}`}
              onClick={(e) => goSection(e, n.hash)}
              data-testid={`nav-link-${n.hash.slice(1)}`}
              className="text-[12.5px] tracking-wide text-[color:var(--relumia-muted)] hover:text-[color:var(--relumia-ink)] transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2.5 md:gap-3">
          <Link
            to="/find-a-dermatologist"
            data-testid="nav-find-derm-cta"
            className={`hidden md:inline-flex items-center gap-2 rounded-full border border-[color:var(--relumia-ink)]/25 px-4 py-2.5 text-[12px] font-medium tracking-wide text-[color:var(--relumia-ink)] hover:border-[color:var(--relumia-gold)] hover:bg-white/60 transition-colors ${
              location.pathname === "/find-a-dermatologist" ? "border-[color:var(--relumia-gold)] bg-white/60" : ""
            }`}
          >
            <MapPin size={15} strokeWidth={1.9} className="shrink-0 text-gold" />
            <span className="hidden lg:inline">Find a Dermatologist</span>
            <span className="lg:hidden">Find Doctor</span>
          </Link>

          <Link
            to="/skin-analysis"
            data-testid="nav-clinical-assessment-cta"
            className={`skin-ai-cta group inline-flex items-center gap-2 rounded-full px-4 md:px-5 py-2.5 text-[12px] md:text-[12.5px] font-medium tracking-wide text-white ${
              location.pathname === "/skin-analysis" ? "ring-2 ring-[color:var(--relumia-gold)]" : ""
            }`}
          >
            <Stethoscope size={16} strokeWidth={1.9} className="shrink-0" />
            <span className="hidden sm:inline">Clinical Assessment</span>
            <span className="sm:hidden">Assessment</span>
          </Link>

          <a
            href="/#skin-ai"
            onClick={(e) => goSection(e, "#skin-ai")}
            data-testid="nav-skin-ai-cta"
            className="skin-ai-cta group inline-flex items-center gap-2 rounded-full px-4 md:px-5 py-2.5 text-[12px] md:text-[12.5px] font-medium tracking-wide text-white"
          >
            <ScanFace size={16} strokeWidth={1.9} className="shrink-0" />
            <span className="hidden sm:inline">Know Your Skin Health</span>
            <span className="sm:hidden">Skin AI</span>
          </a>

          <a
            href="/#sunscreen-coach"
            onClick={(e) => goSection(e, "#sunscreen-coach")}
            data-testid="nav-sunguard-cta"
            className="skin-ai-cta group inline-flex items-center gap-2 rounded-full px-4 md:px-5 py-2.5 text-[12px] md:text-[12.5px] font-medium tracking-wide text-white"
          >
            <Sun size={16} strokeWidth={1.9} className="shrink-0" />
            <span className="hidden sm:inline">SunGuard</span>
            <span className="sm:hidden">SunGuard</span>
          </a>

          <a
            href="/#contact"
            onClick={(e) => goSection(e, "#contact")}
            data-testid="nav-cta"
            className="hidden xl:inline-flex items-center rounded-full bg-[color:var(--relumia-ink)] px-5 py-2.5 text-[12.5px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors"
          >
            Get Relumia
          </a>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden flex flex-col gap-1.5 ml-1"
            aria-label="Menu"
          >
            <span className="block h-px w-6 bg-[color:var(--relumia-ink)]" />
            <span className="block h-px w-6 bg-[color:var(--relumia-ink)]" />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-black/5 px-5 py-6 flex flex-col gap-4">
          {SECTIONS.map((n) => (
            <a
              key={n.hash}
              href={`/${n.hash}`}
              onClick={(e) => goSection(e, n.hash)}
              className="text-sm tracking-wide text-[color:var(--relumia-muted)]"
            >
              {n.label}
            </a>
          ))}
          <Link
            to="/skin-analysis"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-clinical-assessment"
            className="skin-ai-cta inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[13px] font-medium text-white mt-2"
          >
            <Stethoscope size={16} strokeWidth={1.9} /> Clinical Assessment
          </Link>
          <Link
            to="/find-a-dermatologist"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-find-derm"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--relumia-ink)]/25 px-5 py-3 text-[13px] font-medium text-[color:var(--relumia-ink)]"
          >
            <MapPin size={16} strokeWidth={1.9} className="text-gold" /> Find a Dermatologist
          </Link>
          <a
            href="/#skin-ai"
            onClick={(e) => goSection(e, "#skin-ai")}
            className="skin-ai-cta inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[13px] font-medium text-white"
          >
            <ScanFace size={16} strokeWidth={1.9} /> Know Your Skin Health
          </a>
          <a
            href="/#sunscreen-coach"
            onClick={(e) => goSection(e, "#sunscreen-coach")}
            data-testid="nav-mobile-sunguard"
            className="skin-ai-cta inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[13px] font-medium text-white"
          >
            <Sun size={16} strokeWidth={1.9} /> SunGuard
          </a>
          <a
            href="/#contact"
            onClick={(e) => goSection(e, "#contact")}
            className="inline-flex items-center justify-center rounded-full bg-[color:var(--relumia-ink)] px-5 py-3 text-[13px] tracking-wide text-white"
          >
            Get Relumia
          </a>
        </div>
      )}
    </motion.header>
  );
}
